<script setup lang="ts">
import { gql } from '@apollo/client'
import { ref } from 'vue'

import { apolloClient } from './apolloClient'

const username = ref('')
const password = ref('')
const loading = ref(false)
/** Centered under "Sign In" — wrong credentials, GraphQL/network errors (blue, like mockup) */
const authError = ref('')
/** Shown under the username field when empty on submit */
const usernameError = ref('')
/** Shown under the password field when empty on submit */
const passwordError = ref('')

const LOGIN_MUTATION = gql`
  mutation Login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      success
      message
    }
  }
`

function clearFieldErrors(): void {
  usernameError.value = ''
  passwordError.value = ''
}

async function submitLogin(): Promise<void> {
  authError.value = ''
  clearFieldErrors()

  const u = username.value.trim()
  let hasFieldError = false
  if (!u) {
    usernameError.value = 'The Username field is required.'
    hasFieldError = true
  }
  if (!password.value) {
    passwordError.value = 'The Password field is required.'
    hasFieldError = true
  }
  if (hasFieldError) {
    return
  }

  loading.value = true
  try {
    interface LoginMutationData {
      login?: { success?: boolean; message?: string }
    }

    const result = await apolloClient.mutate<LoginMutationData>({
      mutation: LOGIN_MUTATION,
      variables: {
        username: u,
        password: password.value,
      },
    })

    if (result.error) {
      authError.value = 'Email or password mismatch'
      return
    }

    const login = result.data?.login

    if (login !== undefined && login.success === false) {
      authError.value = 'Email or password mismatch'
      return
    }
    window.location.assign('/')
  } catch (e: unknown) {
    const msg =
      e && typeof e === 'object' && 'message' in e
        ? String((e as { message: string }).message)
        : ''
    authError.value = msg || 'Network error. Try again.'
  } finally {
    loading.value = false
  }
}

function onUsernameInput(): void {
  usernameError.value = ''
  authError.value = ''
}

function onPasswordInput(): void {
  passwordError.value = ''
  authError.value = ''
}
</script>

<template>
  <div class="login-page" data-page="login-page">
    <main class="main-container login-page__main">
      <section class="login-page__screen">
        <div class="resolution-wrap">
          <div class="login-page__layout">
            <div class="login-page__brand" aria-label="Pestec">
              <svg class="login-page__logo" viewBox="0 0 151 151" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <g clip-path="url(#login-logo-clip)">
                  <path class="login-page__logo-wordmark" d="M143.809 109.076C141.716 111.461 138.685 112.832 135.506 112.832C129.409 112.832 124.467 107.909 124.467 101.84C124.467 95.771 129.409 90.8485 135.506 90.8485C137.788 90.8485 140.042 91.566 141.901 92.8848C142.255 93.1374 142.328 93.6184 142.066 93.9631L140.412 96.1158C140.166 96.4364 139.708 96.5086 139.378 96.2801C138.227 95.4824 136.903 95.0775 135.506 95.0775C131.755 95.0775 128.717 98.104 128.717 101.84C128.717 105.576 131.755 108.603 135.506 108.603C137.45 108.603 139.265 107.801 140.565 106.354C140.834 106.053 141.293 106.017 141.607 106.274L143.72 107.993C144.054 108.266 144.094 108.751 143.809 109.076ZM105.564 108.514H118.76C119.179 108.514 119.521 108.855 119.521 109.272V111.986C119.521 112.403 119.179 112.743 118.76 112.743C113.198 112.743 107.64 112.743 102.078 112.743C101.66 112.743 101.31 112.403 101.31 111.986C101.31 105.223 101.31 98.4568 101.31 91.6903C101.31 91.2774 101.644 90.9366 102.058 90.9366H118.76C119.179 90.9366 119.521 91.2774 119.521 91.6903V94.4081C119.521 94.825 119.179 95.1657 118.76 95.1657H105.564V99.7235H116.116C116.53 99.7235 116.876 100.064 116.876 100.481V103.199C116.876 103.612 116.53 103.957 116.116 103.957H105.564V108.514ZM37.0138 108.514H50.2143C50.6288 108.514 50.9709 108.855 50.9709 109.272V111.986C50.9709 112.403 50.6288 112.743 50.2143 112.743C44.6524 112.743 39.0905 112.743 33.5286 112.743C33.11 112.743 32.7599 112.403 32.7599 111.986C32.7599 105.223 32.7599 98.4568 32.7599 91.6903C32.7599 91.2774 33.0939 90.9366 33.5125 90.9366H50.2143C50.6288 90.9366 50.9709 91.2774 50.9709 91.6903V94.4081C50.9709 94.825 50.6288 95.1657 50.2143 95.1657H37.0138V99.7235H47.5661C47.9806 99.7235 48.3268 100.064 48.3268 100.481V103.199C48.3268 103.612 47.9847 103.957 47.5661 103.957H37.0138V108.514ZM11.2971 103.965C13.3577 103.965 15.4464 103.965 17.5351 103.965C20.5253 103.965 21.9178 101.796 21.8896 99.5992C21.8615 97.3464 20.3523 95.1858 17.5351 95.1858C15.4746 95.1858 13.3577 95.1577 11.2971 95.1858V103.965ZM17.5351 90.9406C23.0769 90.9406 26.0671 95.1216 26.1516 99.4549C26.2683 103.82 23.427 108.214 17.5351 108.214C15.4464 108.214 13.3577 108.214 11.2971 108.214V111.99C11.2971 112.407 10.9228 112.739 10.5123 112.739H7.78771C7.37721 112.739 7.00293 112.407 7.00293 111.99V91.7183C7.00293 91.3135 7.38124 90.9406 7.78771 90.9406C11.0355 90.9406 14.2873 90.9406 17.5351 90.9406ZM71.512 97.178C70.0914 95.7951 68.3286 94.9613 66.3003 94.8771C65.105 94.825 63.2416 95.1377 62.3804 95.6427C61.4628 96.1839 61.33 97.2061 62.159 97.8996C63.1491 98.7254 65.6201 99.0781 66.5579 99.3106C70.8078 100.357 75.4923 101.868 75.0134 107.103C74.6109 111.533 69.4394 113.012 65.7208 113C62.1872 112.992 59.4224 111.657 56.8467 109.248C56.5368 108.955 56.5167 108.454 56.8145 108.142C57.4423 107.468 58.0782 106.795 58.71 106.121C58.9998 105.813 59.4344 105.801 59.7403 106.097C61.5151 107.821 63.2175 108.735 65.6765 108.751C67.0368 108.763 70.353 108.478 70.7192 106.747C71.1901 104.526 67.0569 103.792 65.6 103.439C63.8252 103.011 61.652 102.626 60.0985 101.62C56.2792 99.1423 56.5368 94.0313 60.4607 91.9147C62.1671 90.9928 64.3323 90.6721 66.2439 90.6801C69.4555 90.6881 72.2405 91.9188 74.4982 94.1315C74.8081 94.4362 74.8081 94.9372 74.5023 95.2419C73.8503 95.8993 73.2466 96.5166 72.5906 97.17C72.2968 97.4667 71.8099 97.4707 71.512 97.178ZM85.2638 111.986V95.1657H79.8992C79.4806 95.1657 79.1385 94.821 79.1385 94.4081V91.6903C79.1385 91.2774 79.4806 90.9366 79.8992 90.9366H94.8784C95.293 90.9366 95.6391 91.2774 95.6391 91.6903V94.4081C95.6391 94.825 95.293 95.1657 94.8784 95.1657H89.5097V111.986C89.5097 112.403 89.1676 112.743 88.7531 112.743H86.0245C85.6059 112.743 85.2638 112.403 85.2638 111.986Z" />
                  <path class="login-page__logo-accent" d="M89.5941 41.4905C96.9872 48.8543 96.9872 60.792 89.5941 68.1517C82.2011 75.5156 70.2201 75.5156 62.827 68.1517C55.434 60.792 55.434 48.8543 62.827 41.4905C70.2201 34.1267 82.2011 34.1267 89.5941 41.4905Z" />
                  <path class="login-page__logo-mark" d="M66.8997 54.1938C67.9541 55.244 67.9541 56.9477 66.8997 58.002C65.8452 59.0522 64.1308 59.0522 63.0764 58.002C62.0179 56.9477 62.0179 55.244 63.0764 54.1938C64.1308 53.1395 65.8452 53.1395 66.8997 54.1938ZM94.3429 44.2925C94.8822 43.9318 95.3973 43.5109 95.8722 43.0659C97.6913 41.3542 100.295 44.493 98.2467 45.9441C97.4861 46.4812 96.6973 46.9623 95.8722 47.3952C97.4378 51.3797 97.7396 55.7611 96.7456 59.9221C97.474 60.0904 98.2024 60.2388 98.9429 60.359C99.8646 60.5113 100.508 61.3572 100.4 62.2831L100.311 63.0488C100.199 64.0469 99.281 64.7404 98.2829 64.5761C97.2929 64.4117 96.2988 64.2073 95.3249 63.9708C94.3147 66.1074 92.9223 68.1117 91.1474 69.8794C90.3506 70.6732 89.5054 71.3907 88.6241 72.0321C89.0708 72.6414 89.5698 73.2106 90.1131 73.7397C90.753 74.3651 90.8214 75.3552 90.2741 76.0607L89.8073 76.666C89.1593 77.4958 87.952 77.584 87.1954 76.8544C86.3502 76.0407 85.5855 75.1387 84.9134 74.1807C82.2814 75.3632 79.4562 75.9805 76.6229 76.0286C76.6471 76.7863 76.7356 77.5439 76.8845 78.2895C77.0616 79.1634 76.5585 80.0252 75.7094 80.2978L74.9729 80.5383C73.9708 80.863 72.9244 80.2537 72.7111 79.2275C72.4656 78.057 72.3449 76.8624 72.3408 75.6639C68.3203 74.9102 64.4809 72.9861 61.37 69.8875C58.259 66.7888 56.3272 62.9646 55.5666 58.96C54.3673 58.956 53.168 58.8317 51.9928 58.5912C50.9625 58.3788 50.3508 57.3365 50.6768 56.3384L50.9183 55.6048C51.1919 54.759 52.0572 54.2579 52.9346 54.4343C53.6831 54.5826 54.4438 54.6708 55.2044 54.6949C55.2527 51.8688 55.8725 49.0548 57.0557 46.4331C56.0938 45.7677 55.1923 45.0021 54.3794 44.1643C53.6429 43.4106 53.7314 42.204 54.5645 41.5627L55.1722 41.0977C55.8805 40.5525 56.8746 40.6207 57.5024 41.258C58.0296 41.7992 58.6011 42.2922 59.2128 42.7372C59.8567 41.8553 60.5731 41.0135 61.37 40.2238C63.1448 38.452 65.1611 37.069 67.3102 36.0629C67.0687 35.0928 66.8675 34.1067 66.7025 33.1206C66.5415 32.1264 67.2337 31.2125 68.2358 31.1002L69.0045 31.012C69.9342 30.9038 70.7833 31.5452 70.9363 32.4631C71.057 33.1967 71.2059 33.9263 71.3749 34.6478C75.5484 33.6617 79.9512 33.9624 83.9516 35.5217C84.3862 34.6999 84.8692 33.9102 85.4085 33.1566C86.8653 31.1163 90.0165 33.7098 88.2981 35.5217C87.8513 35.9947 87.4288 36.5078 87.0666 37.045C90.0407 38.8168 92.5641 41.3302 94.3429 44.2925ZM85.1952 49.179L66.0143 68.28C69.0246 70.601 72.6427 71.7595 76.2607 71.7595C80.5589 71.7595 84.8571 70.128 88.125 66.873C91.7229 63.2893 93.4736 58.3106 92.9384 53.2878C90.1695 52.4661 87.5334 51.0951 85.1952 49.179ZM62.9838 65.2615L82.1607 46.1565C80.2369 43.8275 78.8606 41.2019 78.0355 38.444C72.9928 37.9108 67.9984 39.6586 64.4045 43.2423C61.1285 46.5053 59.4905 50.7825 59.4905 55.0556C59.4905 58.6594 60.6536 62.2631 62.9838 65.2615ZM77.126 64.3796C76.0716 63.3294 74.3611 63.3294 73.3027 64.3796C72.2483 65.4339 72.2483 67.1376 73.3027 68.1878C74.3611 69.2421 76.0716 69.2421 77.126 68.1878C78.1844 67.1376 78.1844 65.4339 77.126 64.3796ZM88.2981 54.4984C87.2436 53.4482 85.5292 53.4482 84.4748 54.4984C83.4163 55.5527 83.4163 57.2564 84.4748 58.3106C85.5292 59.3609 87.2436 59.3609 88.2981 58.3106C89.3525 57.2564 89.3525 55.5527 88.2981 54.4984ZM85.4246 62.1188C84.3661 61.0645 82.6557 61.0645 81.6013 62.1188C80.5428 63.169 80.5428 64.8767 81.6013 65.927C82.6557 66.9772 84.3661 66.9772 85.4246 65.927C86.479 64.8767 86.479 63.169 85.4246 62.1188ZM76.8201 43.0659C77.8745 44.1162 77.8745 45.8238 76.8201 46.8741C75.7617 47.9283 74.0513 47.9283 72.9928 46.8741C71.9384 45.8238 71.9384 44.1162 72.9928 43.0659C74.0513 42.0156 75.7617 42.0156 76.8201 43.0659ZM69.1695 45.928C70.228 46.9823 70.228 48.686 69.1695 49.7362C68.1151 50.7905 66.4047 50.7905 65.3462 49.7362C64.2918 48.686 64.2918 46.9823 65.3462 45.928C66.4047 44.8778 68.1151 44.8778 69.1695 45.928Z" />
                </g>
                <defs>
                  <clipPath id="login-logo-clip">
                    <rect width="137" height="82" transform="translate(7 31)"></rect>
                  </clipPath>
                </defs>
              </svg>
            </div>

            <form
              id="login-form"
              class="form-theme login-page__form"
              :class="{ 'login-page__form--sending': loading }"
              action="#"
              novalidate
              @submit.prevent="submitLogin"
            >
            <div class="input-item off-label">
              <label for="login-email">Email</label>
              <input
                id="login-email"
                v-model="username"
                name="loginEmail"
                type="email"
                placeholder="Email"
                autocomplete="email"
                maxlength="100"
                data-msg-valid="Enter a valid email"
                data-msg-req="Email is required"
                data-msg-min="Minimum 5 characters"
                data-msg-max="Maximum 100 characters"
                :class="{ error: !!usernameError }"
                :disabled="loading"
                @input="onUsernameInput"
              />
              <span v-if="usernameError" class="error">{{ usernameError }}</span>
            </div>

            <div class="input-item off-label">
              <label for="login-password">Password</label>
              <input
                id="login-password"
                v-model="password"
                name="loginPassword"
                type="password"
                placeholder="Password"
                autocomplete="current-password"
                maxlength="100"
                data-msg-req="Password is required"
                data-msg-min="Minimum 4 characters"
                data-msg-max="Maximum 100 characters"
                :class="{ error: !!passwordError }"
                :disabled="loading"
                @input="onPasswordInput"
              />
              <span v-if="passwordError" class="error">{{ passwordError }}</span>
            </div>

            <div class="login-page__form-actions">
              <button
                class="btn__theme-1 btn-big login-page__submit"
                :class="{ 'login-page__submit--loading': loading }"
                type="submit"
                :disabled="loading"
              >
                <span>{{ loading ? 'Signing in…' : 'Sign In' }}</span>
              </button>
            </div>
              <span v-if="authError" class="error" role="alert">{{ authError }}</span>
            </form>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>
